import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { api } from '../../lib/api'
import { featureBillingQuotaLabel, resolveFeatureBillingGate } from '../../lib/featureBillingGate'

function unavailableQuotaText(gate) {
  switch (gate?.reason) {
    case 'FEATURE_NOT_ENABLED':
      return '미제공'
    case 'SUBSCRIPTION_EXPIRED':
      return '만료'
    case 'SUBSCRIPTION_SUSPENDED':
      return '정지'
    case 'SUBSCRIPTION_REQUIRED':
      return '구독 필요'
    default:
      return '-'
  }
}

/**
 * @param {{
 *   gate?: import('../../lib/featureBillingGate').FeatureBillingGate|null,
 *   featureCode?: string,
 *   enabled?: boolean,
 *   label?: string,
 *   loading?: boolean,
 *   className?: string,
 * }} props
 */
export default function FeatureBillingQuotaBadge({
  gate,
  featureCode,
  enabled = true,
  label = '잔여',
  loading = false,
  className = '',
}) {
  const shouldFetch = enabled && !gate && Boolean(featureCode)
  const billingMeQuery = useQuery({
    queryKey: ['billing-me'],
    queryFn: async () => {
      const res = await api.get('/billing/me', { __silent: true })
      return res.data?.data ?? res.data
    },
    staleTime: 60_000,
    enabled: shouldFetch,
  })

  if (!enabled) return null

  const resolvedGate = gate || (featureCode ? resolveFeatureBillingGate(billingMeQuery.data, featureCode) : null)
  const pending = loading || (shouldFetch && billingMeQuery.isLoading && !billingMeQuery.data)
  const rawQuotaText = pending ? '확인 중' : featureBillingQuotaLabel(resolvedGate)
  const quotaText = !pending && resolvedGate && !resolvedGate.canUse && rawQuotaText === '-'
    ? unavailableQuotaText(resolvedGate)
    : rawQuotaText
  const quotaExhausted = !pending
    && resolvedGate
    && !resolvedGate.unlimited
    && resolvedGate.limit != null
    && resolvedGate.remaining != null
    && Number(resolvedGate.remaining) <= 0
  const statusOnly = resolvedGate?.unlimited
    || (!pending && resolvedGate && !resolvedGate.canUse && rawQuotaText === '-')
  const displayText = quotaExhausted
    ? `소진 ${quotaText}`
    : (statusOnly || quotaText === '-'
      ? quotaText
      : `${label} ${quotaText}`)
  const blocked = !pending && resolvedGate && !resolvedGate.canUse

  return (
    <span
      title={quotaExhausted ? '이번 달 사용 가능 횟수를 모두 사용했습니다.' : undefined}
      className={[
        'inline-flex min-h-[1.75rem] shrink-0 items-center rounded-full border px-2.5 py-1 text-xs font-bold tabular-nums',
        quotaExhausted
          ? 'border-rose-300 bg-rose-50 text-rose-700 ring-2 ring-rose-100 shadow-sm'
          : blocked
          ? 'border-amber-200 bg-amber-50 text-amber-900'
          : 'border-slate-200 bg-white text-slate-700',
        className,
      ].filter(Boolean).join(' ')}
    >
      {displayText}
    </span>
  )
}